import { ApplePay } from '@adyen/adyen-web'
import { useCallback, useEffect, useRef, useState } from 'react'

import { fetchApplePayMerchantSession } from '@/lib/apple-pay-merchant-session'
import { isIframed, verifiedParentHost } from '@/lib/apple-pay-parent-host'
import { AdyenCheckoutStatus } from '@/lib/enums'
import { extractEndDigits, normalizeWalletBrand } from '@/lib/wallet-brand'

import type { AdyenEnvironment, SessionResult } from '@/lib/types'

interface UseApplePayOptions {
  /** Resolves `checkoutshopper-${adyenEnvironment}` for the merchant-session request. See specs/app/index.md::Apple Pay. */
  adyenEnvironment: AdyenEnvironment
  /**
   * The embedder's own declaration of its host, cross-checked against
   * `ancestorOrigins` before it is trusted. Absent → falls back to the
   * browser-reported top-level host directly. See
   * `apple-pay-parent-host.ts::verifiedParentHost` and
   * specs/app/index.md::Apple Pay.
   */
  parentHost?: string
  /** Reads the checkout core created by the Card flow's `mount()`; `null` until then. */
  getCheckout: () => unknown | null
  /** Reads the active Adyen session id, set by the Card flow's `mount()`. */
  getSessionId: () => string | null
  /** Claims the shared session against the internal API. */
  claimSession: (sessionId: string) => Promise<unknown>
  /** Resolves once one of the shared Adyen callbacks settles the session result. */
  waitForSessionResult: () => Promise<SessionResult>
  /** Shared checkout status setter — Apple Pay and Card drive the same status. */
  setStatus: (status: AdyenCheckoutStatus) => void
  /**
   * Writes the shared `brand`/`endDigits` refs the post-payment chain reads
   * (`onPaymentCompletedPayment`'s guard) — the Card form never mounts on a
   * wallet-only payment, so `onBrand`/`onFieldValid` never fire for it. See
   * specs/app/index.md::Apple Pay.
   */
  setWalletBrandInfo: (brand: string, endDigits: string) => void
}

interface UseApplePayReturn {
  /** True once the mounted ApplePay component's `isAvailable()` resolves. */
  applePayAvailable: boolean
  /** See specs/app/index.md::Apple Pay. */
  mountApplePay: (el: HTMLElement) => Promise<void>
  /** See specs/app/index.md::Apple Pay. */
  submitApplePay: () => Promise<SessionResult>
  /** Reads the in-flight claim promise fired by `submitApplePay`, or `null`. See specs/app/index.md::Apple Pay. */
  getPendingClaim: () => Promise<unknown> | null
}

/**
 * Owns the Adyen ApplePay component on top of the checkout core the Card flow
 * (`useAdyenCheckout`) creates. See specs/app/index.md::Apple Pay and Decisions.
 */
export function useApplePay(options: UseApplePayOptions): UseApplePayReturn {
  const applePayComponentRef = useRef<unknown>(null)
  const applePayMountedElRef = useRef<HTMLElement | null>(null)
  const applePayClaimRef = useRef<Promise<unknown> | null>(null)
  const rejectApplePayAttemptRef = useRef<((error: Error) => void) | null>(null)
  const [applePayAvailable, setApplePayAvailable] = useState(false)

  const mountApplePay = useCallback(
    async (el: HTMLElement) => {
      // Re-entry guard: the parent mount effect re-runs on unrelated re-renders. See specs/app/index.md::Apple Pay.
      if (applePayMountedElRef.current === el) return
      // Claimed synchronously before the isAvailable() await below, so two overlapping calls can't both pass the check above.
      applePayMountedElRef.current = el

      const checkout = options.getCheckout() as {
        paymentMethodsResponse: {
          has: (type: string) => boolean
          find: (type: string) => { configuration?: { merchantName?: string; merchantId?: string } } | undefined
        }
      } | null
      if (!checkout) return
      if (!checkout.paymentMethodsResponse.has('applepay')) return

      // Merchant validation is only overridden when the top-level host is confirmed — see specs/app/index.md::Apple Pay.
      const iframed = isIframed()
      const hppHost = iframed ? verifiedParentHost(options.parentHost) : null
      if (iframed && !hppHost) {
        applePayMountedElRef.current = null // unclaim: an env fix must allow a retry
        return
      }

      const applePayConfig = {
        showPayButton: false,
        renderApplePayCodeAs: 'modal', // see specs/app/index.md::Decisions
        onClick: (resolve: () => void) => resolve(),
        ...(hppHost && {
          onValidateMerchant: async (resolve: (session: unknown) => void, reject: (reason?: unknown) => void) => {
            const configuration = checkout.paymentMethodsResponse.find('applepay')?.configuration
            try {
              resolve(
                await fetchApplePayMerchantSession({
                  adyenEnvironment: options.adyenEnvironment,
                  domainName: hppHost,
                  displayName: configuration?.merchantName ?? '',
                  merchantIdentifier: configuration?.merchantId ?? '',
                })
              )
            } catch (error) {
              reject(error)
            }
          },
        }),
        onAuthorized: (
          data: {
            authorizedEvent: { payment: { token: { paymentMethod: { network: string; displayName: string } } } }
          },
          actions: { resolve: () => void; reject: () => void }
        ) => {
          const { network, displayName } = data.authorizedEvent.payment.token.paymentMethod
          const endDigits = extractEndDigits(displayName)
          if (endDigits) {
            options.setWalletBrandInfo(normalizeWalletBrand(network), endDigits)
          } else {
            console.error(
              'useApplePay.mountApplePay onAuthorized: could not parse end digits from displayName',
              displayName
            )
          }
          actions.resolve()
        },
        onError: (error: unknown) => {
          const e = error instanceof Error ? error : new Error(String(error))
          if (e.name !== 'CANCEL') {
            Object.assign(e, { state: { isValid: true } })
            console.error('useApplePay.mountApplePay onError', e)
          }
          rejectApplePayAttemptRef.current?.(e)
        },
      }
      // biome-ignore lint/suspicious/noExplicitAny: Adyen SDK 6 types are loose
      const applePay = new ApplePay(checkout as any, applePayConfig as any)

      try {
        await applePay.isAvailable()
      } catch {
        applePayMountedElRef.current = null // unclaim: this element never got a component, a retry must be allowed
        setApplePayAvailable(false)
        return
      }

      applePay.mount(el)
      applePayComponentRef.current = applePay
      setApplePayAvailable(true)
    },
    [options.getCheckout, options.setWalletBrandInfo, options.adyenEnvironment, options.parentHost]
  )

  const submitApplePay = useCallback(async (): Promise<SessionResult> => {
    const sessionId = options.getSessionId()
    const applePayComponent = applePayComponentRef.current as { submit: () => void } | null
    if (!sessionId || !applePayComponent) {
      throw new Error('useApplePay.submitApplePay: component not mounted')
    }

    options.setStatus(AdyenCheckoutStatus.SUBMITTING)

    const attempt = new Promise<never>((_, reject) => {
      rejectApplePayAttemptRef.current = reject
    })

    applePayClaimRef.current = options.claimSession(sessionId)
    applePayClaimRef.current.catch(() => {}) // see specs/app/index.md::Decisions
    applePayComponent.submit()

    const sessionResultPromise = options.waitForSessionResult()

    try {
      const result = await Promise.race([sessionResultPromise, attempt])
      options.setStatus(result.refusal ? AdyenCheckoutStatus.FAILED : AdyenCheckoutStatus.COMPLETED)
      return result
    } catch (error) {
      applePayClaimRef.current = null
      options.setStatus(AdyenCheckoutStatus.READY)
      throw error
    } finally {
      rejectApplePayAttemptRef.current = null
    }
  }, [options.getSessionId, options.claimSession, options.waitForSessionResult, options.setStatus])

  const getPendingClaim = useCallback(() => applePayClaimRef.current, [])

  useEffect(() => {
    return () => {
      const applePayComponent = applePayComponentRef.current as { unmount?: () => void } | null
      try {
        applePayComponent?.unmount?.()
      } catch {}
    }
  }, [])

  return { applePayAvailable, mountApplePay, submitApplePay, getPendingClaim }
}
